import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Plus, LogOut, Sparkles, Clock, CheckCircle2, MessageCircle, User, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { divinationLabel } from '@/data/divination';
import { reqNo } from '@/data/format';
import { birthDateText, birthTimeText } from '@/data/birth';
import CityPicker from '@/components/CityPicker';
import { LANGUAGE_OPTIONS } from '@/data/languages';
import NotificationBell from '@/components/NotificationBell';
import { regionToCurrency, symbolOf } from '@/data/money';

const ClientDashboard = () => {
  const { user, logout, checkAuth, config, setDisplayCurrency, refreshProfile } = useAuth();
  const free = config?.free_mode;
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    nickname: '',
    email: '',
    phone_country_code: '+852',
    phone_number: '',
    languages: [],
    region: '',
  });

  useEffect(() => {
    fetchData();
    // Light auto-refresh so newly-arrived answers/orders show without a manual reload.
    // Background ticks fail silently — no red-toast loop on a flaky connection.
    const id = setInterval(() => fetchData(true), 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/client/profile`, { withCredentials: true });
      if (res.data) {
        setProfile(res.data);
        // Default the display currency to the client's region, unless they've picked one.
        try {
          if (!localStorage.getItem('fm_currency') && res.data.region) {
            setDisplayCurrency(regionToCurrency(res.data.region));
          }
        } catch (e) { /* ignore */ }
      } else {
        // Prompt new clients to complete contact info; nickname/email default to the login account.
        setProfileData((p) => ({ ...p, nickname: user?.name || '', email: user?.email || '' }));
        setShowProfileDialog(true);
      }
    } catch (error) {
      console.error('Error fetching client profile:', error);
    }
  };

  const toggleLanguage = (lang) => {
    setProfileData((prev) => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter((l) => l !== lang)
        : [...prev.languages, lang],
    }));
  };

  const openEditProfile = () => {
    setProfileData({
      nickname: profile?.nickname || user?.name || '',
      email: user?.email || '',
      phone_country_code: profile?.phone_country_code || '+852',
      phone_number: profile?.phone_number || '',
      languages: profile?.languages || [],
      region: profile?.region || '',
    });
    setProfileMenuOpen(false);
    setShowProfileDialog(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!profileData.nickname.trim()) { toast.error('請填寫暱稱'); return; }
    if (profileData.nickname.trim().length > 20) { toast.error('暱稱不可超過 20 字'); return; }
    if (!profileData.region.trim()) { toast.error('請選擇居住地區'); return; }
    if (!profileData.languages.length) { toast.error('請選擇至少一種語言'); return; }
    setSavingProfile(true);
    try {
      const res = await axios.put(
        `${API}/client/profile`,
        {
          nickname: profileData.nickname.trim(),
          email: profileData.email.trim(),
          phone_country_code: profileData.phone_country_code,
          phone_number: profileData.phone_number,
          languages: profileData.languages,
          region: profileData.region,
        },
        { withCredentials: true }
      );
      setProfile(res.data);
      setShowProfileDialog(false);
      refreshProfile();   // open the app-wide mandatory-profile gate
      try { if (!localStorage.getItem('fm_currency') && res.data.region) setDisplayCurrency(regionToCurrency(res.data.region)); } catch (e) { /* ignore */ }
      toast.success('個人資料已儲存');
    } catch (error) {
      toast.error(error.response?.data?.detail || '儲存失敗');
    } finally {
      setSavingProfile(false);
    }
  };

  const fetchData = async (silent = false) => {
    try {
      const [requestsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/requests`, { withCredentials: true }),
        axios.get(`${API}/orders`, { withCredentials: true })
      ]);
      setRequests(requestsRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (!silent) toast.error('無法載入資料');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Escape hatch from the mandatory-profile gate: a freshly-registered user who picked
  // the wrong role can switch (allowed only while the account has no profile/activity).
  const switchToAstrologer = async () => {
    try {
      await axios.post(`${API}/auth/switch-role`, { role: 'astrologer' }, { withCredentials: true });
      await checkAuth();
      navigate('/astrologer/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '切換失敗');
    }
  };

  const getStatusBadge = (status, expired, ordersCount = 0) => {
    if (expired) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs rounded-sm bg-[#E5E0D8] text-[#9A9A9A]">
          已過期
        </span>
      );
    }
    if (status === 'open' && ordersCount >= 1) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs rounded-sm bg-[#FBE8D8] text-[#C9772B]">
          已選 {ordersCount} 位
        </span>
      );
    }
    const badges = {
      open: { text: '開放中', class: 'bg-[#F5F0E6] text-[#C93B2B]' },
      completed: { text: '已完成', class: 'bg-[#E5E0D8] text-[#1A1A1A]' }
    };
    const badge = badges[status] || badges.open;
    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs rounded-sm ${badge.class}`}>
        {badge.text}
      </span>
    );
  };

  const openCount = requests.filter((r) => r.status === 'open' && !r.expired && (r.orders_count || 0) === 0).length;
  const atOpenLimit = openCount >= 3;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-[#E5E0D8] px-6 md:px-12 lg:px-24 py-4 flex justify-between items-center">
        <Link to="/client/dashboard">
          <h2 className="text-xl font-serif text-[#C93B2B]">命理配對平台</h2>
        </Link>
        <div className="flex items-center gap-4">
          <NotificationBell />
          <Popover open={profileMenuOpen} onOpenChange={setProfileMenuOpen}>
            <PopoverTrigger asChild>
              <button
                className="flex items-center gap-2 text-sm text-[#1A1A1A] hover:text-[#C93B2B] transition-colors"
                data-testid="profile-menu-trigger"
              >
                <span className="w-7 h-7 rounded-sm bg-[#C93B2B] text-white flex items-center justify-center"><User className="w-4 h-4" strokeWidth={1.5} /></span>
                <span>{profile?.nickname || user?.name}</span>
                <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 rounded-sm p-4">
              <p className="font-serif text-lg text-[#1A1A1A] mb-1">{profile?.nickname || user?.name}</p>
              {profile?.nickname && <p className="text-xs text-[#9A9A9A] mb-3">命理師會以「{profile.nickname}」稱呼你</p>}
              {profile ? (
                <div className="space-y-1 text-xs text-[#5C5C5C] mb-3">
                  <p>電郵：{profile.email}</p>
                  <p>語言：{(profile.languages || []).join('、')}</p>
                  <p>居住地區：{profile.region}</p>
                </div>
              ) : (
                <p className="text-xs text-[#5C5C5C] mb-3">尚未填寫個人資料</p>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={openEditProfile}
                className="w-full rounded-sm border-[#C93B2B] text-[#C93B2B] hover:bg-[#F5F0E6]"
                data-testid="edit-client-profile-btn"
              >
                {profile ? '編輯個人資料' : '填寫個人資料'}
              </Button>
            </PopoverContent>
          </Popover>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="rounded-sm"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            登出
          </Button>
        </div>
      </nav>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        {/* My Consultations Section */}
        {orders.length > 0 && (
          <div className="mb-12" data-testid="my-consultations-section">
            <div className="flex items-center gap-2 mb-6">
              <MessageCircle className="w-5 h-5 text-[#C93B2B]" strokeWidth={1.5} />
              <h2 className="text-xl sm:text-2xl font-serif text-[#1A1A1A]">我的諮詢室</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {orders.map((order) => (
                <div
                  key={order.order_id}
                  className="bg-white border border-[#E5E0D8] rounded-sm p-5 hover:shadow-md transition-shadow cursor-pointer flex flex-col"
                  onClick={() => navigate(`/chat/${order.order_id}`)}
                  data-testid={`order-card-${order.order_id}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="inline-flex items-center border border-[#C93B2B] text-[#C93B2B] px-2 py-1 text-xs rounded-none font-serif tracking-widest">
                      {divinationLabel(order)}
                    </div>
                    {!free && (
                      <span className="text-xs text-[#5C5C5C]">
                        {symbolOf(order.currency)} {Number(order.amount).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    {order.other_party_avatar && (
                      <img src={order.other_party_avatar} alt="" className="w-8 h-8 rounded-sm object-cover flex-shrink-0" />
                    )}
                    <p className="text-sm text-[#1A1A1A] font-medium">
                      命理師：{order.other_party_name}
                    </p>
                  </div>
                  {order.client_subject_name && (
                    <p className="text-xs text-[#5C5C5C] mb-3">
                      求測者：{order.client_subject_name}
                    </p>
                  )}
                  <p className="text-xs text-[#5C5C5C] mb-4">
                    {free ? '開通時間' : '付款時間'}：{new Date(order.paid_at).toLocaleString('zh-TW')}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full rounded-sm border-[#C93B2B] text-[#C93B2B] hover:bg-[#F5F0E6] mt-auto"
                    data-testid={`enter-chat-${order.order_id}`}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    進入諮詢室
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#1A1A1A] mb-2">
              我的求測單
            </h1>
            <p className="text-base text-[#5C5C5C]">
              管理你的命理諮詢請求
            </p>
          </div>
          {atOpenLimit ? (
            <div className="text-right">
              <Button
                disabled
                className="bg-[#C93B2B] text-white rounded-sm shadow-sm opacity-50 cursor-not-allowed"
                data-testid="create-request-btn"
              >
                <Plus className="mr-2" strokeWidth={1.5} />
                新增求測單
              </Button>
              <p className="text-xs text-[#9A9A9A] mt-1">已達 3 張開放中求測單上限</p>
            </div>
          ) : (
            <Link to="/client/create-request">
              <Button
                className="bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm shadow-sm"
                data-testid="create-request-btn"
              >
                <Plus className="mr-2" strokeWidth={1.5} />
                新增求測單
              </Button>
            </Link>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-[#5C5C5C]">載入中...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white border border-[#E5E0D8] rounded-sm p-12 text-center">
            <Sparkles className="w-12 h-12 text-[#C93B2B] mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-xl font-serif text-[#1A1A1A] mb-2">尚無求測單</h3>
            <p className="text-sm text-[#5C5C5C] mb-6">
              開始你的命理之旅，提交你的第一個求測單
            </p>
            <Link to="/client/create-request">
              <Button 
                className="bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm"
                data-testid="create-first-request-btn"
              >
                建立求測單
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {requests.map((req) => (
              <div 
                key={req.request_id} 
                className="bg-white border border-[#E5E0D8] rounded-sm p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/client/request/${req.request_id}`)}
                data-testid={`request-card-${req.request_id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-2">
                    <div className="inline-flex items-center border border-[#C93B2B] text-[#C93B2B] px-2 py-1 text-xs rounded-none font-serif tracking-widest">
                      {divinationLabel(req)}
                    </div>
                    {req.seq && <span className="text-xs text-[#9C9C9C] font-mono">{reqNo(req.seq)}</span>}
                  </div>
                  {getStatusBadge(req.status, req.expired, req.orders_count)}
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-[#5C5C5C]">
                    <Clock className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {new Date(req.created_at).toLocaleDateString('zh-TW')}
                  </div>
                  <p className="text-sm text-[#1A1A1A] font-medium">
                    姓名：{req.client_name}
                  </p>
                  <p className="text-sm text-[#5C5C5C]">
                    出生：{birthDateText(req)}　{birthTimeText(req)}
                  </p>
                  <p className="text-sm text-[#5C5C5C]">
                    出生地區：{req.region}
                  </p>
                  {req.answer_count > 0 ? (
                    <p className="text-sm font-medium pt-1">
                      <span className="text-[#C93B2B]">{req.answer_count} 位命理師已回答</span>
                    </p>
                  ) : req.status === 'open' ? (
                    <p className="text-sm font-medium pt-1">
                      <span className="text-[#9A9A9A]">{req.expired ? '已過期，未有回答' : `尚待命理師回答…${req.days_left != null ? ` · 有效 ${req.days_left} 日` : ''}`}</span>
                    </p>
                  ) : null}
                </div>

                <Button 
                  variant="outline" 
                  className="w-full rounded-sm border-[#E5E0D8] hover:bg-[#F5F0E6]"
                  data-testid={`view-request-${req.request_id}`}
                >
                  {req.status === 'open' && req.answer_count > 0 ? '查看並選擇命理師' : '查看詳情'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showProfileDialog} onOpenChange={(o) => { if (profile) setShowProfileDialog(o); }}>
        <DialogContent
          className={`rounded-sm max-w-lg max-h-[90vh] overflow-y-auto ${!profile ? '[&>button]:hidden' : ''}`}
          onInteractOutside={(e) => { if (!profile) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (!profile) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{profile ? '編輯個人資料' : '完善個人資料'}</DialogTitle>
            <DialogDescription>
              {profile
                ? '填寫聯絡資料，方便平台通知你求測單的最新進度（不會公開給命理師）'
                : '開始使用前，請先完善個人資料（不會公開給命理師）'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div>
              <Label htmlFor="client-nickname">暱稱 *</Label>
              <Input
                id="client-nickname"
                value={profileData.nickname}
                onChange={(e) => setProfileData({ ...profileData, nickname: e.target.value })}
                placeholder="例：小明"
                className="rounded-sm mt-2"
                maxLength={20}
                required
                data-testid="client-nickname"
              />
              <p className="text-xs text-[#9A9A9A] mt-1 text-right">{profileData.nickname.length} / 20</p>
            </div>

            <div>
              <Label htmlFor="client-email">聯絡電郵</Label>
              <Input
                id="client-email"
                type="email"
                value={user?.email || ''}
                disabled
                className="rounded-sm mt-2 disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="client-email"
              />
              <p className="text-xs text-[#9A9A9A] mt-1">已使用你的 Google 帳戶電郵（已驗證，不可更改）</p>
            </div>

            <div>
              <Label>語言 *（可多選）</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {LANGUAGE_OPTIONS.map((lang) => {
                  const active = profileData.languages.includes(lang);
                  return (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => toggleLanguage(lang)}
                      className={`px-3 py-2 rounded-sm border text-sm transition-colors ${
                        active
                          ? 'bg-[#C93B2B] text-white border-[#C93B2B]'
                          : 'bg-white text-[#1A1A1A] border-[#E5E0D8] hover:bg-[#F5F0E6]'
                      }`}
                      data-testid={`client-lang-${lang}`}
                    >
                      {lang}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>居住地區 *</Label>
              <p className="text-xs text-[#9A9A9A] mt-1">而家住嘅城市（用嚟預設價錢顯示貨幣）；出生地喺開求測單時另外填</p>
              <div className="mt-2">
                <CityPicker
                  value={profileData.region}
                  onChange={(v) => setProfileData({ ...profileData, region: v })}
                  testId="client-region"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={savingProfile}
              className="w-full bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm shadow-sm"
              data-testid="save-client-profile-btn"
            >
              {savingProfile ? '儲存中...' : '儲存'}
            </Button>
          </form>
          {!profile && (
            <div className="mt-1 pt-3 border-t border-[#E5E0D8] flex justify-between items-center text-xs">
              <button type="button" onClick={switchToAstrologer} className="text-[#C93B2B] hover:underline" data-testid="switch-to-astrologer">
                其實想做命理師？改用命理師身份
              </button>
              <button type="button" onClick={handleLogout} className="text-[#5C5C5C] hover:underline" data-testid="gate-logout">
                登出
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <footer className="px-6 md:px-12 lg:px-24 py-6 text-center text-xs text-[#9A9A9A]">
        <Link to="/privacy" className="hover:text-[#C93B2B] hover:underline">隱私政策</Link>
        <span className="mx-2">·</span>
        <Link to="/terms" className="hover:text-[#C93B2B] hover:underline">服務條款</Link>
      </footer>
    </div>
  );
};

export default ClientDashboard;